import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouteLoaderData } from 'react-router';

import type { TRecommendationsModalElement } from '@shared/dream-components';
import { ProprietaryRecommendationsModalElement, RecommendationsModalElement } from '@shared/dream-components';
import { ClientOnly } from 'remix-utils/client-only';

import useGlobalModals from '~/components/GlobalModals/hooks/useGlobalModals';
import { RecommendationsModalContext, RecommendationsModalForm } from '~/components/RecommendationsModalAction';
import getRecommendationsModalTranslations from '~/components/RecommendationsModalAction/translations';
import useSignupRedirects from '~/hooks/useUtils/useSignupRedirects';
import type { RootLoaderData } from '~/root';

interface Props {
  node: TRecommendationsModalElement;
  children: ReactNode | ReactNode[];
}

const RecommendationsModal = ({ node, children }: Props) => {
  const { t } = useTranslation();
  const { handleCloseModal, currentModal } = useGlobalModals();
  const { site } = useRouteLoaderData('root') as RootLoaderData;

  const { nextStep, nextPage } = useSignupRedirects();

  const onOpenChange = (value: boolean) => {
    if (!value) {
      if (nextStep) {
        nextPage();
      } else {
        handleCloseModal({ preventScrollReset: true });
      }
    }
  };

  return (
    <ClientOnly fallback={null}>
      {() =>
        site.use_custom_recommendations_modal ? (
          <RecommendationsModalElement
            element={node}
            isOpen={currentModal === 'recommendations'}
            onOpenChange={onOpenChange}
          >
            {children}
          </RecommendationsModalElement>
        ) : (
          <RecommendationsModalContext
            onOpenChange={onOpenChange}
            networkUiEnabled={site.recommendations_network_ui_enabled}
          >
            {({ email, isOpen, isLoading, recommendations, submittedIds, onSubmitSuccess }) => (
              <ProprietaryRecommendationsModalElement
                email={email}
                isLoading={isLoading}
                v3Layout={site.recommendations_network_ui_enabled}
                recommendations={recommendations}
                submittedIds={submittedIds}
                onSubmitSuccess={onSubmitSuccess}
                isOpen={isOpen}
                onOpenChange={onOpenChange}
                theme={site.primary_theme}
                translations={getRecommendationsModalTranslations(t)}
                SubscribeForm={RecommendationsModalForm}
              />
            )}
          </RecommendationsModalContext>
        )
      }
    </ClientOnly>
  );
};

export default RecommendationsModal;
